import { useEffect, useMemo, useState } from 'react'
import { Button, Form, Table, Badge } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { IMessageTemplate } from '../../../types/campaigns'
import { ChannelBadge } from './ChannelBadge'
import { SUPPORTED_CHANNELS, CHANNEL_LABELS } from '../../../constants/campaigns'
import { parseId } from '../../../helpers'
import { useLocalStorage } from '../../../hooks/useLocalStorage'
import { TEMPLATE_FILTER_KEYS } from '../../../constants/campaigns'
import { PaginationSection } from '../../shared/PaginationSection'
import { PaginationUtils } from '../../shared/PaginationUtils'
import { MessageDescriptor, useIntl, FormattedMessage } from 'react-intl'
import {
  CAMPAIGN_MESSAGES as M,
  EM_DASH,
  fullName,
  ICON_VARIANTS,
  STAR_EMPTY,
  STAR_FILLED,
  withIcon,
} from '../campaignMessages'

type Props = {
  templates: IMessageTemplate[]
  onCreate: () => void
  onEdit: (template: IMessageTemplate) => void
  onDuplicate: (template: IMessageTemplate) => void
  onDelete: (template: IMessageTemplate) => void
  onRestore: (template: IMessageTemplate) => void
  onToggleBlueprint: (template: IMessageTemplate, isBlueprint: boolean) => void
  showArchived: boolean
  onToggleShowArchived: (value: boolean) => void
  canManageBlueprints?: boolean
  canSeeOwner?: boolean
  isSuperAdmin?: boolean
  isAdmin?: boolean
  currentUserId?: string | number
}

/** La opción "todos" se traduce; los nombres de canal no (guía §17.3). */
const channelOptions: Array<{ value: string; label?: string; message?: MessageDescriptor }> = [
  { value: '', message: M.allChannels },
  ...SUPPORTED_CHANNELS.map(channel => ({
    value: channel,
    label: CHANNEL_LABELS[channel]
  }))
]

export const TemplatesPanel = ({
  templates,
  onCreate,
  onEdit,
  onDuplicate,
  onDelete,
  onRestore,
  onToggleBlueprint,
  showArchived,
  onToggleShowArchived,
  canManageBlueprints = false,
  canSeeOwner = false,
  isSuperAdmin = false,
  isAdmin = false,
  currentUserId,
}: Props) => {
  const intl = useIntl()
  const navigate = useNavigate()
  const [search, setSearch] = useLocalStorage(TEMPLATE_FILTER_KEYS.search, '')
  const [channelFilter, setChannelFilter] = useLocalStorage(TEMPLATE_FILTER_KEYS.channel, '')
  const [blueprintFilter, setBlueprintFilter] = useLocalStorage<'all' | 'blueprints' | 'regular'>(
    TEMPLATE_FILTER_KEYS.blueprint,
    'all'
  )
  const [ownerFilter, setOwnerFilter] = useLocalStorage(TEMPLATE_FILTER_KEYS.owner, '')
  const [orgFilter, setOrgFilter] = useLocalStorage(TEMPLATE_FILTER_KEYS.org, '')
  const [currentPage, setCurrentPage] = useState(1)
  const [first, setFirst] = useLocalStorage(TEMPLATE_FILTER_KEYS.pageSize, 20)

  // Blueprints are shared assets: nobody deletes them from here. To remove one,
  // an admin unmarks it as blueprint (☆) first. Everything else is deletable by
  // its owner or by an administrator.
  const canDelete = (template: IMessageTemplate): boolean => {
    if (template.isBlueprint) return false
    if (isAdmin || isSuperAdmin) return true
    if (!currentUserId || !template.owner?.id) return false
    return String(parseId(template.owner.id)) === String(parseId(currentUserId))
  }

  const getChannels = (template: IMessageTemplate): string[] => {
    let meta = template.metadata
    if (typeof meta === 'string') {
      try {
        meta = JSON.parse(meta)
      } catch (e) {
        meta = {}
      }
    }

    const enabled = meta?.enabled_channels
    if (Array.isArray(enabled) && enabled.length > 0) {
      return enabled
    }

    return template.channel ? [template.channel] : []
  }

  const ownerOptions = useMemo(() => {
    const seen = new Map<string, string>()
    templates.forEach((t) => {
      if (t.owner) {
        seen.set(t.owner.id, `${t.owner.firstName} ${t.owner.lastName} - ${t.owner.email}`)
      }
    })
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]))
  }, [templates])

  const orgOptions = useMemo(() => {
    const seen = new Map<string, string>()
    templates.forEach((t) => {
      if (t.organization) {
        seen.set(t.organization.id, t.organization.name)
      }
    })
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]))
  }, [templates])

  const filteredTemplates = useMemo(() => {
    return templates.filter((template) => {
      const templateChannels = getChannels(template)
      const matchesChannel = !channelFilter || templateChannels.includes(channelFilter)
      const text = `${template.name} ${template.description ?? ''} ${template.subject ?? ''}`.toLowerCase()
      const matchesSearch = !search || text.includes(search.toLowerCase())
      const matchesBlueprint =
        blueprintFilter === 'all' ||
        (blueprintFilter === 'blueprints' && template.isBlueprint) ||
        (blueprintFilter === 'regular' && !template.isBlueprint)
      const matchesOwner = !ownerFilter || template.owner?.id === ownerFilter
      const matchesOrg = !orgFilter || template.organization?.id === orgFilter
      return matchesChannel && matchesSearch && matchesBlueprint && matchesOwner && matchesOrg
    })
  }, [templates, channelFilter, search, blueprintFilter, ownerFilter, orgFilter])

  const blueprintCount = templates.filter(t => t.isBlueprint).length

  // Client-side pagination: the templates query returns the full list.
  const totalElements = filteredTemplates.length
  const totalPages = Math.max(1, Math.ceil(totalElements / first))
  const { start, end } = PaginationUtils.getPaginationRange(currentPage, first, totalElements)

  // Any filter change can shrink the list past the current page.
  useEffect(() => {
    setCurrentPage(1)
  }, [search, channelFilter, blueprintFilter, ownerFilter, orgFilter])

  // Owner/org filters persist as ids. If a stored id is no longer among the
  // loaded templates the select renders blank while still filtering everything
  // out, so drop it instead of leaving an invisible active filter.
  useEffect(() => {
    if (templates.length === 0) return
    if (ownerFilter && !ownerOptions.some(([id]) => id === ownerFilter)) {
      setOwnerFilter('')
    }
    if (orgFilter && !orgOptions.some(([id]) => id === orgFilter)) {
      setOrgFilter('')
    }
  }, [templates, ownerOptions, orgOptions, ownerFilter, orgFilter, setOwnerFilter, setOrgFilter])

  const hasActiveFilters =
    !!search || !!channelFilter || blueprintFilter !== 'all' || !!ownerFilter || !!orgFilter

  const clearFilters = () => {
    setSearch('')
    setChannelFilter('')
    setBlueprintFilter('all')
    setOwnerFilter('')
    setOrgFilter('')
  }

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages)
  }, [currentPage, totalPages])

  const paginatedTemplates = useMemo(
    () => filteredTemplates.slice((currentPage - 1) * first, currentPage * first),
    [filteredTemplates, currentPage, first]
  )

  return (
    <div className='card mt-5'>
      <div className='card-header border-0 pt-5 d-flex align-items-center justify-content-between'>
        <div>
          <h4 className='mb-1'>
            <FormattedMessage {...M.navTemplates} />
            {blueprintCount > 0 && (
              <Badge bg='warning' text='dark' className='ms-2 fs-8'>
                {withIcon(
                  STAR_FILLED,
                  intl.formatMessage(M.templatesBlueprintCount, {count: blueprintCount})
                )}
              </Badge>
            )}
          </h4>
          <span className='text-muted'><FormattedMessage {...M.templatesSubtitle} /></span>
        </div>
        <div className='d-flex align-items-center gap-3'>
          <Form.Check
            type='switch'
            id='templates-show-archived'
            label={intl.formatMessage(M.showArchived)}
            checked={showArchived}
            onChange={(event) => onToggleShowArchived(event.target.checked)}
          />
          <Button size='sm' onClick={onCreate} className='btn-primary'>
            <FormattedMessage {...M.templatesCreate} />
          </Button>
        </div>
      </div>
      <div className='card-body border-top'>
        {hasActiveFilters && (
          <div className='d-flex align-items-center gap-2 mb-3'>
            <span className='text-muted fs-8'>
              <FormattedMessage
                {...M.templatesShowingFiltered}
                values={{shown: totalElements, total: templates.length}}
              />
            </span>
            <Button size='sm' variant='light' onClick={clearFilters}>
              <FormattedMessage {...M.templatesClearFilters} />
            </Button>
          </div>
        )}
        <div className='row g-3 mb-4'>
          <div className={canSeeOwner ? 'col-md-4' : 'col-md-5'}>
            <Form.Control
              type='search'
              placeholder={intl.formatMessage(M.templatesSearch)}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <div className='col-md-2'>
            <Form.Select value={channelFilter} onChange={(event) => setChannelFilter(event.target.value)}>
              {channelOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.message ? intl.formatMessage(option.message) : option.label}
                </option>
              ))}
            </Form.Select>
          </div>
          <div className='col-md-2'>
            <Form.Select
              value={blueprintFilter}
              onChange={(event) => setBlueprintFilter(event.target.value as 'all' | 'blueprints' | 'regular')}
            >
              <option value='all'>{intl.formatMessage(M.templatesFilterAll)}</option>
              <option value='blueprints'>
                {withIcon(STAR_FILLED, intl.formatMessage(M.templatesFilterBlueprints))}
              </option>
              <option value='regular'>{intl.formatMessage(M.templatesFilterRegular)}</option>
            </Form.Select>
          </div>
          {canSeeOwner && (
            <div className='col-md-3'>
              <Form.Select value={ownerFilter} onChange={(event) => setOwnerFilter(event.target.value)}>
                <option value=''>{intl.formatMessage(M.templatesAllUsers)}</option>
                {ownerOptions.map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </Form.Select>
            </div>
          )}
          {isSuperAdmin && (
            <div className='col-md-3'>
              <Form.Select value={orgFilter} onChange={(event) => setOrgFilter(event.target.value)}>
                <option value=''>{intl.formatMessage(M.templatesAllOrgs)}</option>
                {orgOptions.map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </Form.Select>
            </div>
          )}
        </div>
        {filteredTemplates.length === 0 ? (
          <div className='campaigns-placeholder justify-content-start px-0'>
            {intl.formatMessage(templates.length === 0 ? M.templatesEmpty : M.templatesNoMatch)}
          </div>
        ) : (
          <>
          <PaginationSection
            first={first}
            setFirst={(n) => {
              setFirst(n)
              setCurrentPage(1)
            }}
            currentPage={currentPage}
            totalPages={totalPages}
            start={start}
            end={end}
            totalPolicies={totalElements}
            updatePage={setCurrentPage}
            PaginationUtils={PaginationUtils}
          />
          <div className='table-responsive'>
            <Table hover className='align-middle mb-0'>
              <thead>
                <tr>
                  <th><FormattedMessage {...M.name} /></th>
                  <th><FormattedMessage {...M.channels} /></th>
                  <th><FormattedMessage {...M.subject} /></th>
                  <th><FormattedMessage {...M.templatesColVariables} /></th>
                  {canSeeOwner && <th><FormattedMessage {...M.templatesColCreatedBy} /></th>}
                  {isSuperAdmin && <th><FormattedMessage {...M.organization} /></th>}
                  <th><FormattedMessage {...M.templatesColUpdated} /></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {paginatedTemplates.map((template) => (
                  <tr key={template.id}>
                    <td>
                      <div className='d-flex align-items-center gap-2'>
                        <div className='fw-semibold'>{template.name}</div>
                        {template.isBlueprint && (
                          <Badge bg='warning' text='dark' title={intl.formatMessage(M.templatesBlueprintTooltip)}>
                            {withIcon(STAR_FILLED, intl.formatMessage(M.templatesBlueprint))}
                          </Badge>
                        )}
                        {template.isActive === false && (
                          <Badge
                            bg='secondary'
                            title={intl.formatMessage(M.templatesArchivedTooltip)}
                          >
                            <FormattedMessage {...M.templatesArchivedBadge} />
                          </Badge>
                        )}
                        {(template.usage?.total ?? 0) > 0 && (
                          <Badge
                            bg='light'
                            text='dark'
                            title={intl.formatMessage(M.templatesUsageTooltip, {
                              campaigns: template.usage?.campaigns ?? 0,
                              automations:
                                (template.usage?.automations ?? 0) +
                                (template.usage?.automationTemplates ?? 0),
                            })}
                          >
                            <FormattedMessage {...M.templatesInUseBadge} />
                          </Badge>
                        )}
                        {template.isActive === false && (
                          <Badge
                            bg='secondary'
                            title={intl.formatMessage(M.templatesArchivedTooltip)}
                          >
                            <FormattedMessage {...M.templatesArchivedBadge} />
                          </Badge>
                        )}
                        {(template.usage?.total ?? 0) > 0 && (
                          <Badge
                            bg='light'
                            text='dark'
                            title={intl.formatMessage(M.templatesUsageTooltip, {
                              campaigns: template.usage?.campaigns ?? 0,
                              automations:
                                (template.usage?.automations ?? 0) +
                                (template.usage?.automationTemplates ?? 0),
                            })}
                          >
                            <FormattedMessage {...M.templatesInUseBadge} />
                          </Badge>
                        )}
                      </div>
                      <div
                        className='text-muted fs-8'
                        style={{maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}
                        title={template.description || undefined}
                      >
                        {template.description || intl.formatMessage(M.noDescription)}
                      </div>
                    </td>
                    <td>
                      <div className='d-flex flex-column gap-1 align-items-start'>
                        {getChannels(template).map(ch => (
                          <ChannelBadge key={ch} channel={ch} />
                        ))}
                      </div>
                    </td>
                    <td>
                      <div
                        style={{maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}
                        title={template.subject || undefined}
                      >
                        {template.subject || EM_DASH}
                      </div>
                    </td>
                    <td>
                      <div className='text-muted'>
                        {template.variables && (Array.isArray(template.variables) ? template.variables.length : 0) > 0
                          ? (Array.isArray(template.variables) ? template.variables.join(', ') : template.variables)
                          : EM_DASH}
                      </div>
                    </td>
                    {canSeeOwner && (
                      <td>
                        {template.owner
                          ? fullName(template.owner.firstName, template.owner.lastName)
                          : EM_DASH}
                      </td>
                    )}
                    {isSuperAdmin && (
                      <td>{template.organization?.name ?? EM_DASH}</td>
                    )}
                    <td>{template.updatedAt ? new Date(template.updatedAt).toLocaleString() : EM_DASH}</td>
                    <td className='text-end'>
                      <Button
                        size='sm'
                        variant={template.isBlueprint ? 'warning' : 'outline-secondary'}
                        onClick={() => onToggleBlueprint(template, !template.isBlueprint)}
                        className='me-2'
                        title={intl.formatMessage(
                          template.isBlueprint ? M.templatesUnmarkBlueprint : M.templatesMarkBlueprint
                        )}
                        disabled={!canManageBlueprints}
                      >
                        {template.isBlueprint ? STAR_FILLED : STAR_EMPTY}
                      </Button>
                      <Button
                        size='sm'
                        variant='light'
                        onClick={() => onDuplicate(template)}
                        className='me-2'
                        title={intl.formatMessage(M.templatesDuplicate)}
                      >
                        <i className='bi bi-files'></i>
                      </Button>
                      <Button
                        size='sm'
                        variant='light'
                        onClick={() => onEdit(template)}
                        className='me-2'
                        disabled={template.isBlueprint && !canManageBlueprints}
                      >
                        <FormattedMessage {...M.edit} />
                      </Button>
                      <Button
                        size='sm'
                        variant='primary'
                        onClick={() => navigate(`/templates/edit/${template.id}`)}
                        className='me-2'
                      >
                        {template.isBlueprint && !canManageBlueprints
                          ? intl.formatMessage(M.templatesViewBlueprint)
                          : withIcon(ICON_VARIANTS, intl.formatMessage(M.templatesVariants))}
                      </Button>
                      {canDelete(template) &&
                        (template.isActive === false ? (
                          <Button
                            size='sm'
                            variant='outline-success'
                            onClick={() => onRestore(template)}
                            title={intl.formatMessage(M.templatesRestoreTooltip)}
                          >
                            <i className='bi bi-arrow-counterclockwise'></i>
                          </Button>
                        ) : (
                          <Button
                            size='sm'
                            variant='outline-danger'
                            onClick={() => onDelete(template)}
                            title={
                              (template.usage?.total ?? 0) > 0
                                ? intl.formatMessage(M.templatesDeleteInUseTooltip)
                                : intl.formatMessage(M.templatesDeleteTooltip)
                            }
                          >
                            <i className='bi bi-trash'></i>
                          </Button>
                        ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          </>
        )}
      </div>
    </div>
  )
}
